import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';

import Header from '../Header';
import useForm from '../../../hooks/useForm';
import EditTier from '../../modals/EditTier';
import DeleteTier from '../../modals/DeleteTier';
import ButtonLoader from '../../../pages/ButtonLoader';
import SpinnerLoader from '../../common/SpinnerLoader';
import { NUMBERS } from '../../../resources/number-constants';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getCopyrightText, handleSuccess, renderErrorToast } from '../../../utility/helpers';
import { deleteBtn, editBtn, tierEmpty } from '../../../assets/images/images';
import { filtersSelector, storeAdminTiersFiltersData } from '../../../store/features/filters';
import { addTierAction, adminSelector, getTiersList, updateTierAction } from '../../../store/features/admin';

const Main = () => {
    const dispatch = useAppDispatch();
    const missedErrorMsg = 'You missed this.';
    const [loading, setLoading] = useState(false); 
    const [showLoader, setShowLoader] = useState(false);
    const [showDeleteTier, setShowDeleteTier] = useState(false);
    const [changeFilters, setChangeFilters] = useState(false);
    const [deleteTierId, setDeleteTierId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1); // State for pagination
    const [formValues, setFormValues] = useState({ name: '', downloadsLimit: '', profileViewsLimit: '', jobCreatesLimit: '', mask: false });
    const [errors, setErrors] = useState({ name: '', downloadsLimit: '', profileViewsLimit: '', jobCreatesLimit: '' }); // State to manage validation errors

    const [editTierId, setEditTierId] = useState(null);
    const [showEditTierModal, setShowEditTierModal] = useState(false);
    const [selectedTier, setSelectedTier] = useState({ name: '', downloadsLimit: '', profileViewsLimit: '', jobCreatesLimit: '', mask: false });

    const { tiersFilters } = useAppSelector(filtersSelector);
    const { adminTiers: { results = [], tiersCount = 0 } } = useAppSelector(adminSelector);

    const requestPayload = (data: any) => {
        return {
            name: data?.name,
            no_of_downloads: data?.downloadsLimit,
            no_of_profile_views: data?.profileViewsLimit,
            no_of_job_creation: data?.jobCreatesLimit,
            mask: !!data?.mask,
        };
    };

    const validateForm = () => {
        const fieldErrors: any = {};
        if (!formValues.name.trim()) {
            fieldErrors.name = missedErrorMsg;
        }
        if (!formValues.downloadsLimit) {
            fieldErrors.downloadsLimit = missedErrorMsg;
        } else if (Number(formValues.downloadsLimit) < 0) {
            renderErrorToast('Number of downloads must be 0 or greater.'); // Display toast message
            return false;
        }
        if (!formValues.profileViewsLimit) {
            fieldErrors.profileViewsLimit = missedErrorMsg;
        } else if (Number(formValues.profileViewsLimit) < 0) {
            renderErrorToast('Number of profile`s view must be 0 or greater.'); // Display toast message
            return false;
        }
        if (!formValues.jobCreatesLimit) {
            fieldErrors.jobCreatesLimit = missedErrorMsg;
        } else if (Number(formValues.jobCreatesLimit) < 0) {
            renderErrorToast('Number of jobs create must be 0 or greater.'); // Display toast message
            return false;
        }
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const handleAddTier = () => {
        if (!validateForm()) {
            return; // Prevent submission if form is invalid
        }

        const payload = requestPayload(formValues);
        setShowLoader(true);
        dispatch(addTierAction(payload))
            .unwrap()
            .then((res: any) => {
                handleSuccess(res?.detail);
                setShowLoader(false);
                setFormValues({ name: '', downloadsLimit: '', profileViewsLimit: '', jobCreatesLimit: '', mask: false }); // Reset form values
                setErrors({ name: '', downloadsLimit: '', profileViewsLimit: '', jobCreatesLimit: '' }); // Reset errors
                refreshTiersList(); // Refresh the tier list after adding
            })
            .catch((error: any) => {
                console.info('Error:', error);
                setShowLoader(false);
            });
    };

    // Refresh the tier list
    const refreshTiersList = async () => {
        setLoading(true);
        await dispatch(getTiersList({ page: 1, limit: 20 })); // Reset to page 1 after adding a tier
        setLoading(false);
        setCurrentPage(1);
    };

    const { values } = useForm(
        {
            page: tiersFilters?.page || 1,
        },
        {}
    );

    const queryParams = useMemo(() => ({
        page: values.page || 1,
        limit: 20
    }), [values.page]);

    useEffect(() => {
        const saveFiltersData = () => {
            dispatch(storeAdminTiersFiltersData(queryParams));
        };

        const fetchData = async () => {
            setLoading(true);
            await dispatch(getTiersList(queryParams));
            setLoading(false);
        };

        fetchData();

        if (changeFilters) {
            saveFiltersData();
            setChangeFilters(false);
        }
    }, [dispatch, queryParams, changeFilters]);

    // Handle form input changes
    const handleInputChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: type === 'checkbox' ? checked : value,
        }));
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '', // Clear error message when user starts typing
        }));
    };

    const handleEditTierClick = (tier: any) => {
        setSelectedTier({
            name: tier.name,
            downloadsLimit: tier.no_of_downloads.toString(),
            profileViewsLimit: tier.no_of_profile_views.toString(),
            jobCreatesLimit: tier.no_of_job_creation.toString(),
            mask: !!tier.mask,
        });
        setEditTierId(tier?.id);
        setShowEditTierModal(true);
    };

    const handleSaveEditTier = (updatedTier: any) => {
        const payload = {
            name: updatedTier.name,
            no_of_downloads: parseInt(updatedTier.downloadsLimit, 10),
            no_of_profile_views: parseInt(updatedTier.profileViewsLimit, 10),
            no_of_job_creation: parseInt(updatedTier.jobCreatesLimit, 10),
            mask: !!updatedTier.mask,
        };

        dispatch(updateTierAction({ payload, id: editTierId }))
            .unwrap()
            .then((res: any) => {
                handleSuccess(res?.detail || 'Tier updated successfully.');
                refreshTiersList();
            })
            .catch((error: any) => {
                console.info('Error updating tier:', error);
            });
    };

    const handleDeleteTier = (tierId: any) => {
        setDeleteTierId(tierId);
        setShowDeleteTier(true);
    };


    // Load more tiers for pagination
    const loadMoreTiers = () => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        dispatch(getTiersList({ page: nextPage, limit: 20 }));
    };
    return (
        <>
            <header className="header admin-header">
                <Container fluid>
                    <Row>
                        <Col>
                            <Header />
                        </Col>
                    </Row>
                </Container>
            </header>

            <div className="tier-main-wrap">
                <div className='container'>
                    <div className="add-tier-wrap">
                        <div className="tier-name">
                            <Form.Group className="form-group">
                                <Form.Control
                                    type="text"
                                    name="name"
                                    placeholder="Enter New Plan Name"
                                    value={formValues.name}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.name}
                                />
                                {errors.name && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name}
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </div>

                        <div className="download-input">
                            <Form.Group className="form-group">
                                <div className='download-input'>
                                <Form.Control
                                    type="number"
                                    name="downloadsLimit"
                                    placeholder="Number of Downloads"
                                    value={formValues.downloadsLimit}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.downloadsLimit}
                                />
                                {errors.downloadsLimit && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.downloadsLimit}
                                    </Form.Control.Feedback>
                                )}
                                </div>

                                <div className='download-input'>
                                <Form.Control
                                    type="number"
                                    name="profileViewsLimit"
                                    placeholder="Number of Profile View"
                                    value={formValues.profileViewsLimit}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.profileViewsLimit}
                                />
                                {errors.profileViewsLimit && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.profileViewsLimit}
                                    </Form.Control.Feedback>
                                )}
                                </div>

                                <div className='download-input'>
                                <Form.Control
                                    type="number"
                                    name="jobCreatesLimit"
                                    placeholder="Number of Job Create"
                                    value={formValues.jobCreatesLimit}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.jobCreatesLimit}
                                />
                                {errors.jobCreatesLimit && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.jobCreatesLimit}
                                    </Form.Control.Feedback>
                                )}
                                </div>

                                <div className='download-input'>
                                    <div className='mask-control' data-tooltip='Mask candidate personal details until a premium plan is selected.'>
                                        <span className='mask-label'>Mask</span>
                                        <input
                                            id='mask-toggle'
                                            name='mask'
                                            type='checkbox'
                                            className='mask-toggle'
                                            checked={!!formValues.mask}
                                            onChange={handleInputChange}
                                        />
                                        <label htmlFor='mask-toggle' className='mask-switch' aria-label='toggle mask'></label>
                                    </div>
                                </div>
                             </Form.Group>
                        </div>

                        <Button className='submit scale' onClick={handleAddTier} disabled={showLoader}>
                            {showLoader ?
                                <ButtonLoader /> :
                                'Add Plan'
                            }
                        </Button>
                    </div>
                </div>

                <div className="tier-card-wrap">
                    <div className="container">
                        {(!loading && (results?.length > 0)) && (
                            <div className="tier-inner-wrap">
                                {results.map((tier: any, index: any) => (
                                    <div className="tier-card" key={index}>
                                        <span className='tier-card-title'>
                                            {(tier.name.length > NUMBERS.TWELVE) ? (
                                                <span className='tier-card-title tooltip-text'>{tier.name}</span>
                                            ) : (
                                                <span className='tier-card-title'>{tier.name}</span>
                                            )}
                                        </span>
                                        {(() => { const isAdvanced = (tier?.name || '').trim().toLowerCase() === 'sbr2th advanced'; return (
                                            <>
                                                <span className='tier-card-subhead'>
                                                    {isAdvanced ? 'Unlimited' : `${tier.no_of_downloads} Download${((tier.no_of_downloads > 1) || (tier.no_of_downloads === 0)) ? 's' : ''}`}
                                                </span>
                                                <span className='tier-card-subhead'>
                                                    {isAdvanced ? 'Unlimited' : `${tier.no_of_profile_views} Profile View${((tier.no_of_profile_views > 1) || (tier.no_of_profile_views === 0)) ? 's' : ''}`}
                                                </span>
                                                <span className='tier-card-subhead'>
                                                    {isAdvanced ? 'Unlimited' : `${tier.no_of_job_creation} Job Create${((tier.no_of_job_creation > 1) || (tier.no_of_job_creation === 0)) ? 's' : ''}`}
                                                </span>
                                                <span className='tier-card-subhead'>
                                                    Mask: {tier.mask ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </>
                                        ); })()}
                                        <span className='tier-card-number'>{tier.assigned_users}</span>
                                        <div className="tier-card-footer">
                                            <span className='tier-card-users'>
                                                User{((tier.assigned_users > 1) || (tier.assigned_users === 0)) ? 's' : ''}
                                            </span>
                                            <div className="tier-btn-wrap">
                                                <Button className='link-btn' variant='link'>
                                                    <img src={editBtn} alt="edit" onClick={() => handleEditTierClick(tier)} className='tier-btn' />
                                                </Button>
                                                {(tier.assigned_users === 0) && (
                                                    <Button className='link-btn' onClick={() => handleDeleteTier(tier.id)} variant='link'>
                                                        <img src={deleteBtn} alt="delete" className='tier-btn' />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(results?.length < tiersCount) && (
                                    <Button className='load-more' onClick={loadMoreTiers}>
                                        Load More
                                    </Button>
                                )}
                            </div>
                        )}
                        {(!loading && !results?.length) && (
                            <div className="no-data-found no-company">
                                <img src={tierEmpty} alt="no data" className='no-data-img' />
                                <h3 className='no-data-title'>
                                    You haven’t created any plan yet!
                                </h3>
                                <p className='no-data-des'>It seems you haven't set up a plan yet. Begin offering<br />download access today by creating your plan.</p>
                            </div>
                        )}
                        {loading && (
                            <SpinnerLoader />
                        )}
                    </div>
                </div>
            </div>

            <div className="admin-footer">
                <span className='admin-footer-copyright'>{getCopyrightText()}</span>
            </div>

            {showEditTierModal && (
                <EditTier
                    show={showEditTierModal}
                    onClose={() => setShowEditTierModal(false)}
                    onSave={handleSaveEditTier}
                    selectedTier={selectedTier}
                />
            )}

            {showDeleteTier && (
                <DeleteTier
                    showDeleteTier={showDeleteTier}
                    setShowDeleteTier={setShowDeleteTier}
                    refreshTiersList={refreshTiersList}
                    deleteTierId={deleteTierId}
                />
            )}
        </>
    );
};

export default Main;
